import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Image, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { InvitationFormValues } from '@/types/invitation-schema';
import GiftSuggestions from './GiftSuggestions';

interface Props {
  invitationId: string | undefined;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
const VALID_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic'];

const GiftsTab = ({ invitationId }: Props) => {
  const form = useFormContext<InvitationFormValues>();
  const { user } = useAuth();
  const bankEnabled = form.watch('bank_transfer_enabled');
  const qrEnabled = form.watch('qr_transfer_enabled');
  const qrImageUrl = form.watch('qr_image_url');
  const [uploadingQr, setUploadingQr] = useState(false);
  const qrInputRef = useRef<HTMLInputElement>(null);

  const uploadQrImage = async (file: File): Promise<void> => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Solo se permiten imágenes (JPG, PNG, WebP, HEIC)');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('La imagen no debe superar 5 MB');
      return;
    }
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !VALID_EXTENSIONS.includes(fileExt)) {
      toast.error('Extensión no válida');
      return;
    }

    if (!invitationId || !user) return;

    setUploadingQr(true);
    const fileName = `${user.id}/${invitationId}/qr-${Date.now()}.${fileExt}`;
    const { error: uploadError } = await supabase.storage
      .from('invitation-photos')
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      toast.error('Error al subir la imagen QR');
      setUploadingQr(false);
      return;
    }

    const { data: urlData } = supabase.storage
      .from('invitation-photos')
      .getPublicUrl(fileName);

    form.setValue('qr_image_url', urlData.publicUrl, { shouldDirty: true });
    setUploadingQr(false);
    if (qrInputRef.current) qrInputRef.current.value = '';
  };

  const handleQrFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadQrImage(file);
  };

  const removeQrImage = () => {
    form.setValue('qr_image_url', '', { shouldDirty: true });
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Configuración de Regalos</CardTitle>
          <CardDescription>
            Opciones para que tus invitados puedan hacerte un regalo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="gifts_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mensaje para los invitados</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="El mejor regalo es su presencia, pero si desean hacernos un obsequio..."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gift_registry_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Link de mesa de regalos</FormLabel>
                <FormControl>
                  <Input placeholder="https://listaderegalos.com/..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bank_transfer_enabled"
            render={({ field }) => (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Transferencia bancaria</p>
                  <p className="text-sm text-muted-foreground">
                    Mostrar datos bancarios para transferencia
                  </p>
                </div>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </div>
            )}
          />

          {bankEnabled && (
            <div className="grid gap-4 md:grid-cols-3 pl-4">
              <FormField
                control={form.control}
                name="bank_account_holder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Titular de la cuenta</FormLabel>
                    <FormControl>
                      <Input placeholder="Juan Pérez" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bank_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Banco</FormLabel>
                    <FormControl>
                      <Input placeholder="Banco Nacional" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bank_account_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de cuenta / CLABE</FormLabel>
                    <FormControl>
                      <Input placeholder="1234567890" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          <FormField
            control={form.control}
            name="qr_transfer_enabled"
            render={({ field }) => (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Transferencia QR</p>
                  <p className="text-sm text-muted-foreground">
                    Mostrar imagen QR para pago
                  </p>
                </div>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </div>
            )}
          />

          {qrEnabled && (
            <div className="pl-4 space-y-3">
              <input
                ref={qrInputRef}
                type="file"
                accept="image/*"
                onChange={handleQrFileChange}
                className="hidden"
              />
              {qrImageUrl ? (
                <div className="relative inline-block">
                  <img
                    src={qrImageUrl}
                    alt="QR de pago"
                    className="w-40 h-40 object-contain border rounded-lg bg-white p-2"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 h-6 w-6"
                    onClick={removeQrImage}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors border-muted-foreground/30 hover:border-primary/50 ${
                    uploadingQr ? 'opacity-60 pointer-events-none' : ''
                  } ${!invitationId ? 'opacity-50 pointer-events-none' : ''}`}
                  onClick={() => invitationId && qrInputRef.current?.click()}
                >
                  <Image className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  {uploadingQr ? (
                    <p className="text-sm text-muted-foreground">Subiendo…</p>
                  ) : !invitationId ? (
                    <p className="text-sm text-muted-foreground">
                      Guarda la invitación primero para subir la imagen QR
                    </p>
                  ) : (
                    <>
                      <p className="text-sm font-medium">Haz clic para subir tu imagen QR</p>
                      <p className="text-xs text-muted-foreground">
                        JPG, PNG, WebP, HEIC · hasta 5 MB
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <FormField
            control={form.control}
            name="cash_envelope_enabled"
            render={({ field }) => (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Efectivo en sobre</p>
                  <p className="text-sm text-muted-foreground">
                    Indicar que aceptas efectivo
                  </p>
                </div>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </div>
            )}
          />
        </CardContent>
      </Card>

      {invitationId && <GiftSuggestions invitationId={invitationId} />}
    </div>
  );
};

export default GiftsTab;
